import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Clock, AlertCircle, PackagePlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { RouterOutputs } from '@/lib/trpc';

type Summary = RouterOutputs['stockAlerts']['getStockIntelligence']['summary'];

/**
 * Which recommendations-table filter a summary card toggles. `null` = no card
 * filter active. Shared with the page (which owns the state) and
 * ReorderRecommendations (which applies the predicate). Predicates mirror the
 * backend summary-count definitions (stock-alerts.service.ts).
 */
export type SummaryFilterKey =
  | 'outOfStock'
  | 'stockingOutSoon'
  | 'lowStock'
  | 'incoming'
  | null;

interface Props {
  summary: Summary | undefined;
  isLoading: boolean;
  activeFilter: SummaryFilterKey;
  onToggleFilter: (key: Exclude<SummaryFilterKey, null>) => void;
}

export function StockAlertsSummaryCards({
  summary,
  isLoading,
  activeFilter,
  onToggleFilter,
}: Props) {
  const cards: Array<{
    filterKey: Exclude<SummaryFilterKey, null>;
    title: string;
    value: number;
    subtitle: string;
    icon: typeof AlertCircle;
    accent: string;
    bg: string;
    iconBg: string;
    ring: string;
  }> = [
    {
      filterKey: 'outOfStock',
      title: 'Out of Stock',
      value: summary?.outOfStock ?? 0,
      subtitle: 'Needs immediate attention',
      icon: AlertCircle,
      accent: 'text-red-600',
      bg: 'bg-red-50',
      iconBg: 'bg-red-100',
      ring: 'ring-red-400',
    },
    {
      filterKey: 'stockingOutSoon',
      title: 'Stocking Out Soon',
      value: summary?.stockingOutSoon ?? 0,
      subtitle: 'Within 7 days at current sell rate',
      icon: AlertTriangle,
      accent: 'text-amber-600',
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-100',
      ring: 'ring-amber-400',
    },
    {
      filterKey: 'lowStock',
      title: 'Low Stock',
      value: summary?.lowStock ?? 0,
      subtitle: 'At or below notify threshold',
      icon: Clock,
      accent: 'text-yellow-600',
      bg: 'bg-yellow-50',
      iconBg: 'bg-yellow-100',
      ring: 'ring-yellow-400',
    },
    {
      filterKey: 'incoming',
      title: 'Incoming Stock',
      value: summary?.incomingStock ?? 0,
      subtitle: `Across ${summary?.incomingOrderCount ?? 0} SKUs on PO`,
      icon: PackagePlus,
      accent: 'text-emerald-600',
      bg: 'bg-emerald-50',
      iconBg: 'bg-emerald-100',
      ring: 'ring-emerald-400',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => {
        const isActive = activeFilter === card.filterKey;
        return (
          <button
            key={card.title}
            type="button"
            disabled={isLoading}
            aria-pressed={isActive}
            onClick={() => onToggleFilter(card.filterKey)}
            className="block w-full text-left rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-default"
          >
            <Card
              className={cn(
                card.bg,
                'transition-shadow motion-reduce:transition-none',
                !isLoading && 'hover:shadow-md',
                isActive && `ring-2 ${card.ring}`,
              )}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {card.title}
                  {isActive && (
                    <span className="ml-1 font-normal text-muted-foreground/80">
                      (filtered)
                    </span>
                  )}
                </CardTitle>
                <div className={`rounded-md p-1.5 ${card.iconBg}`}>
                  <card.icon className={`h-4 w-4 ${card.accent}`} />
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <div className={`text-3xl font-bold ${card.accent}`}>
                    {card.value.toLocaleString()}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {card.subtitle}
                </p>
              </CardContent>
            </Card>
          </button>
        );
      })}
    </div>
  );
}
